// Activity Log tab — unified reverse-chronological timeline from
// GET /api/leads/:id/activity-log (tenant-scoped). Merges calls, SMS/email,
// notes, disposition changes and appointments into one feed. A sticky pill bar
// narrows the feed in memory (no refetch) and shows per-category counts.

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone, MessageSquare, Mail, StickyNote, Bot, Calendar, GitBranch, Activity, Filter } from 'lucide-react';
import { apiGet } from '../../../lib/api.js';
import { fmtDate, fmtFromNow } from '../../../lib/format.js';

const ICONS = {
  call: Phone,
  sms: MessageSquare,
  email: Mail,
  note: StickyNote,
  ai_note: Bot,
  appointment: Calendar,
  disposition: GitBranch,
};

// Pill id → event types it covers.
export const FILTERS = [
  { id: 'all', label: 'All', types: null },
  { id: 'calls', label: 'Calls', types: ['call'] },
  { id: 'sms', label: 'SMS', types: ['sms'] },
  { id: 'email', label: 'Email', types: ['email'] },
  { id: 'notes', label: 'Notes', types: ['note', 'ai_note'] },
  { id: 'appointments', label: 'Appointments', types: ['appointment'] },
  { id: 'dispositions', label: 'Dispositions', types: ['disposition'] },
];

/**
 * @param {Array<{type: string}>} events
 * @returns {Record<string, number>} Event count per filter id.
 */
export function countByFilter(events) {
  const counts = {};
  for (const f of FILTERS) {
    counts[f.id] = f.types ? events.filter((e) => f.types.includes(e.type)).length : events.length;
  }
  return counts;
}

export default function ActivityLogTab({ leadId }) {
  const [filter, setFilter] = useState('all');
  const { data, isLoading } = useQuery({
    queryKey: ['lead-activity-log', leadId],
    queryFn: () => apiGet(`/leads/${leadId}/activity-log`),
    enabled: !!leadId,
  });
  const events = data?.events || [];
  const counts = useMemo(() => countByFilter(events), [events]);
  const active = FILTERS.find((f) => f.id === filter) || FILTERS[0];
  const shown = active.types ? events.filter((e) => active.types.includes(e.type)) : events;

  if (isLoading) return <p className="text-xs text-white/40 p-3">Loading activity…</p>;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="sticky top-0 z-10 bg-civix-bg/95 backdrop-blur-xs pb-2 flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Filter activity">
        <Filter className="w-3.5 h-3.5 text-white/40" />
        {FILTERS.map((f) => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
              filter === f.id
                ? 'bg-civix-purple/25 border-civix-purple/60 text-white'
                : 'border-civix-border text-white/55 hover:text-white/90'
            }`}
          >
            {f.label}{counts[f.id] > 0 ? ` (${counts[f.id]})` : ''}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="text-xs text-white/40 p-3">
          {events.length === 0 ? 'No activity recorded yet.' : `No ${active.label.toLowerCase()} activity.`}
        </p>
      ) : (
        <ul className="space-y-2 p-1 overflow-y-auto flex-1 min-h-0">
          {shown.map((e, i) => {
            const Icon = ICONS[e.type] || Activity;
            return (
              <li key={`${e.type}-${e.at}-${i}`} className="flex gap-3 rounded-lg bg-civix-panel border border-civix-border px-3 py-2">
                <Icon className="w-4 h-4 text-civix-purpleSoft shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-white/85 capitalize">{e.title}</span>
                    <span className="text-[10px] text-white/40 shrink-0" title={e.at ? fmtDate(e.at) : ''}>{fmtFromNow(e.at)}</span>
                  </div>
                  {(e.actor || e.status) && (
                    <div className="text-[10px] text-white/45 mt-0.5 flex items-center gap-2">
                      {e.actor && <span>{e.actor}</span>}
                      {e.status && <span className="px-1.5 rounded bg-white/10 text-white/60">{e.status}</span>}
                    </div>
                  )}
                  {e.detail && <p className="text-[11px] text-white/60 mt-0.5 wrap-break-word">{e.detail}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
